const Cookie = {
  /**
   * 设置 cookie
   * @param name  键
   * @param value 值（会自动 encode）
   * @param days  过期天数，不传则为浏览器会话生命周期
   * @param path  路径，默认 '/'
   * @param domain 域名
   * @param secure  是否仅 HTTPS 下传输
   */
  set(
    name: string,
    value: string,
    days?: number,
    path = "/",
    domain?: string,
    secure = false
  ) {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    if (typeof days === "number") {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      cookie += `;expires=${expires.toUTCString()}`;
    }
    cookie += `;path=${path}`;
    if (domain) {
      cookie += `;domain=${domain}`;
    }
    if (secure) {
      cookie += `;secure`;
    }
    document.cookie = cookie;
  },

  /**
   * 获取 cookie
   * @param name 键
   * @returns 如果不存在返回 null
   */
  get(name: string): string | null {
    const reg = new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`);
    const match = document.cookie.match(reg);
    return match ? decodeURIComponent(match[1]) : null;
  },

  /**
   * 删除 cookie
   * @param name 键
   * @param path 需与 set 时保持一致
   * @param domain 同上
   */
  remove(name: string, path = "/", domain?: string) {
    // 设置一个已过期的时间即可删除
    this.set(name, "", -1, path, domain);
  },

  /**
   * 清空所有 cookie
   * 注意：无法删除 HttpOnly / 跨域 cookie
   */
  clear() {
    document.cookie.split(";").forEach((c) => {
      const eqPos = c.indexOf("=");
      const key = eqPos > -1 ? c.substr(0, eqPos) : c;
      this.remove(decodeURIComponent(key.trim()));
    });
  },
};

/** ---------- Web Storage 操作 ---------- */
type StorageType = Storage; // localStorage | sessionStorage
interface StorageOptions {
  /** 是否序列化成 JSON */
  json?: boolean;
}
function buildStorage(storage: StorageType) {
  return {
    /** 设置 */
    set(key: string, value: any, options: StorageOptions = { json: true }) {
      const v = options.json ? JSON.stringify(value) : String(value);
      storage.setItem(key, v);
    },
    /** 获取 */
    get<T = string>(
      key: string,
      options: StorageOptions = { json: true }
    ): T | null {
      const v = storage.getItem(key);
      if (v === null) return null;
      return options.json ? (JSON.parse(v) as T) : (v as any as T);
    },
    /** 删除 */
    remove(key: string) {
      storage.removeItem(key);
    },
    /** 清空 */
    clear() {
      storage.clear();
    },
  };
}

const LocalStorage = buildStorage(window.localStorage);
const SessionStorage = buildStorage(window.sessionStorage);

export { Cookie, LocalStorage, SessionStorage };

// 用法示例;

// import { Cookie, Local, Session } from '@/utils/webStorage';

// // ———— Cookie ————
// Cookie.set('token', 'abc123', 7);           // 7 天后过期
// const t = Cookie.get('token');
// Cookie.remove('token');
// Cookie.clear();

// // ———— localStorage ————
// Local.set('user', { id: 1, name: '张三' }); // 自动 JSON.stringify
// const user = Local.get<{ id: number; name: string }>('user');
// Local.remove('user');
// Local.clear();

// // ———— sessionStorage ————
// Session.set('flag', true, { json: false }); // 不序列化，直接存 "true"
// const flag = Session.get<string>('flag', { json: false });
// 这样你就有一套统一、可选 JSON 序列化、并且支持设置过期的 Cookie + Storage 工具，后续在项目中随时引入即可。
